import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import DOMPurify from "dompurify";
import { api } from "../lib/api";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";

// Anything the WYSIWYG can legitimately emit — sanitised against XSS on every render.
const SANITIZE_CONFIG = {
  ALLOWED_TAGS: [
    "p", "br", "strong", "em", "s", "u", "code",
    "h1", "h2", "h3", "h4",
    "ul", "ol", "li",
    "blockquote", "hr",
    "a", "span",
  ],
  ALLOWED_ATTR: ["href", "target", "rel"],
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|\/)/i,
};

const looksLikeHtml = (s) => typeof s === "string" && /<\/?[a-z][\s\S]*>/i.test(s);

export default function StaticPage() {
  const params = useParams();
  const loc = useLocation();
  const slug = params.slug || loc.pathname.replace(/^\/+/, "").split("/")[0];
  const nav = useNavigate();
  const [page, setPage] = useState(null);

  useEffect(() => {
    setPage(null);
    if (!slug) { nav("/"); return; }
    api.get(`/pages/${slug}`).then((r) => setPage(r.data)).catch(() => nav("/"));
  }, [slug, nav]);

  const cleanHtml = useMemo(() => {
    if (!page?.body_md) return "";
    if (!looksLikeHtml(page.body_md)) return null;   // legacy plain-text fallback
    return DOMPurify.sanitize(page.body_md, SANITIZE_CONFIG);
  }, [page]);

  if (!page) return <div className="mx-auto max-w-3xl px-6 py-24 text-zinc-400 font-mono-tag">Loading…</div>;

  const seoDesc = page.meta_description
    || (looksLikeHtml(page.body_md) ? page.body_md.replace(/<[^>]+>/g, "").slice(0, 155) : (page.body_md || "").slice(0, 155));

  return (
    <div className="mx-auto max-w-3xl px-6 lg:px-8 py-16" data-testid={`static-page-${page.slug}`}>
      <Seo title={page.meta_title || `${page.title} — BizzAtlas`} description={seoDesc} canonicalPath={`/${page.slug}`} />
      <div className="mb-4">
        <Breadcrumbs items={[{ label: "BizzAtlas", to: "/" }, { label: page.title }]} />
      </div>
      <h1 className="font-display text-5xl tracking-tight mb-2">{page.title}</h1>
      <div className="text-xs text-zinc-500 font-mono-tag mb-10 uppercase tracking-widest">Last updated {page.updated_at?.slice(0, 10)}</div>
      {cleanHtml !== null ? (
        <article
          className="prose prose-zinc max-w-none text-zinc-700 leading-relaxed text-base"
          data-testid="static-page-body"
          dangerouslySetInnerHTML={{ __html: cleanHtml }}
        />
      ) : (
        <article className="prose prose-zinc max-w-none whitespace-pre-line text-zinc-700 leading-relaxed text-base" data-testid="static-page-body">
          {page.body_md}
        </article>
      )}
    </div>
  );
}
